// @ts-nocheck
import {
  Box,
  Grid,
  GridItem,
  Heading,
  Stack,
  Text,
} from "@smoothie-framework/ui";
import { Gutter, WorkflowImage } from "./shared";

const stages = [
  {
    id: "01 / WRITE",
    title: (
      <>
        Every series starts
        <br />
        with your story.
      </>
    ),
    copy: "Give Amanda an idea or a manuscript. Develop your characters, world and plot together, and decide where the story goes.",
    image: "workflow/write.svg",
    alt: "Zequels Write stage preview",
    className: "workflow-preview-write",
  },
  {
    id: "02 / SHAPE",
    title: (
      <>
        Keep them waiting
        <br />
        for the next episode.
      </>
    ),
    copy: "Build an 80-episode script with Amanda. Refine the dialogue, pacing and cliffhangers, and review each episode before moving to the storyboard.",
    image: "workflow/shape.svg",
    alt: "Zequels Shape stage preview",
    className: "workflow-preview-shape",
  },
  {
    id: "03 / PRODUCE",
    title: (
      <>
        Direct the scene.
        <br />
        Produce your series.
      </>
    ),
    copy: "Build a visual storyboard from your script. Choose camera angles, lighting and character details, then render your episodes and prepare them for publishing.",
    image: "workflow/produce.svg",
    alt: "Zequels Produce stage preview",
    className: "workflow-preview-produce",
  },
] as const;

function StageCopy({ stage }: { stage: (typeof stages)[number] }) {
  return (
    <Stack className="workflow-copy" gap={{ base: 4, md: 6 }}>
      <Box
        as="span"
        className="step-pill"
        background="accentEmphasized"
        display="inline-flex"
        w="fit-content"
        rounded="full"
        px={3}
        py={1.75}
      >
        <Text as="span" color="inverse" textStyle="meta">
          {stage.id}
        </Text>
      </Box>
      <Heading level={3} textStyle="h2" color="inverse">
        {stage.title}
      </Heading>
      <Box maxW={105}>
        <Text as="p" color="subtle" color="inverse" textStyle="body-sm">
          {stage.copy}
        </Text>
      </Box>
    </Stack>
  );
}

function WorkflowRow({
  stage,
  reverse = false,
}: {
  stage: (typeof stages)[number];
  reverse?: boolean;
}) {
  if (reverse) {
    return (
      <Grid
        alignItems={{ base: "stretch", lg: "center" }}
        columns={{ base: 1, lg: "minmax(0, 1fr) 420px" }}
        gap={{ base: 7, md: 16 }}
        minH={{ base: 0, lg: 105 }}
      >
        <GridItem
          colStart={{ base: "auto", lg: 2 }}
          rowStart={{ base: "auto", lg: 1 }}
        >
          <StageCopy stage={stage} />
        </GridItem>
        <GridItem
          colStart={{ base: "auto", lg: 1 }}
          rowStart={{ base: "auto", lg: 1 }}
        >
          <WorkflowImage
            className={stage.className}
            image={stage.image}
            alt={stage.alt}
          />
        </GridItem>
      </Grid>
    );
  }

  return (
    <Grid
      alignItems={{ base: "stretch", lg: "center" }}
      columns={{ base: 1, lg: "420px minmax(0, 1fr)" }}
      gap={{ base: 7, md: 16 }}
      minH={{ base: 0, lg: 105 }}
    >
      <GridItem>
        <StageCopy stage={stage} />
      </GridItem>
      <GridItem>
        <WorkflowImage
          className={stage.className}
          image={stage.image}
          alt={stage.alt}
        />
      </GridItem>
    </Grid>
  );
}

export function Workflow() {
  return (
    <Box
      as="section"
      className="workflow section-paper"
      background="surfaceInverse"
      py={{ base: 16, md: 22 }}
    >
      <Gutter>
        <Grid
          alignItems={{ base: "stretch", md: "end" }}
          columns={{ base: 1, md: "1fr 1.2fr" }}
          gap={{ base: 8, md: 20 }}
        >
          <Box>
            <Heading level={2} textStyle="h1" color="inverse" mt={7}>
              From first idea
              <br />
              to final scene.
            </Heading>
          </Box>
          <Box maxW={170}>
            <Text
              as="p"
              mt={{ base: 5, md: 0 }}
              color="inverse"
              textStyle="body"
            >
              Write, shape and produce with Amanda, your AI producer. You make
              the creative decisions at every stage.
            </Text>
          </Box>
        </Grid>
      </Gutter>
      <Stack gap={{ base: 16, md: 22 }} mt={{ base: 11, md: 18 }}>
        {stages.map((stage, index) => (
          <Gutter key={stage.id}>
            <WorkflowRow stage={stage} reverse={index === 1} />
          </Gutter>
        ))}
      </Stack>
    </Box>
  );
}
